import React, { useState } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import moment from 'moment';
import { Space, Button, Spin } from 'antd';
import { TableOutlined, FolderOutlined } from '@ant-design/icons';
import { ProTable, Link, DateDisplay } from 'components/layout';
import { getFundingApplicationStatus, getSort } from 'utils';
import { GET_PROJECT_APPLICATION_SUMMARY_LIST } from 'gql/projectApplications';
import { DOWNLOAD_FUNDING_APPLICATION_PROPOSALS } from 'gql/files';
import {
  renderProTableSearchDepartment,
  renderProTableSearchFaculty,
  renderProTableSearchProjectApplicationSetting,
} from 'components/layout/ProTable';
import { ENUM_PROJECT_APPLICATION_STATUS } from 'constants/form';
import Container from '../Container';
import SelectAllButton from 'components/button/SelectAllButton';
import exportExcel from 'utils/exportExcel';
import dateDiffInMonthsAndDays from 'utils/dateDiffInMonthsAndDays';

const bulkDownloadUrl = process.env.REACT_APP_BULK_DOWNLOAD_URL;
const getTeamMemberName = (teamMember) => {
  if (!teamMember) return '';
  return `${teamMember.name}${
    teamMember.department?.abbreviation
      ? ` (${teamMember.department?.abbreviation})`
      : ''
  }${teamMember.teamMemberType === 'COPL' ? ' [Co-PL]' : ''}`;
};
const getTeamMembers = (team) =>
  team ? team.map(getTeamMemberName).join('\n') : '';

const prepareSummaryList = (ids, data) => {
  const filtered = data.filter((item) => ids.includes(item.id));
  console.log({ filtered });
  return filtered.map((item, idx) => [
    idx + 1,
    item.refNumber ?? '',
    item.firstSubmissionDate
      ? moment(item.firstSubmissionDate).format('DD/MM/YYYY')
      : '',
    item.user?.name ?? '',
    item.user?.title ?? '',
    item.team[0]?.position ?? '',
    item.hostDepartment?.faculty?.abbreviation ?? '',
    item.hostDepartment?.abbreviation ?? '',
    item.user?.email ?? '',
    getTeamMembers(item.team),
    item.title ?? '',
    item.startDate ? moment(item.startDate).format('DD/MM/YYYY') : '',
    item.endDate ? moment(item.endDate).format('DD/MM/YYYY') : '',
    item.startDate && item.endDate
      ? dateDiffInMonthsAndDays(item.startDate, item.endDate)
      : '',
    item.tdgFund ?? '',
    item.matchingFund ?? '',
    item.matchingFundFundingSource ?? '',
    (item.tdgFund ?? 0) + (item.matchingFund ?? 0),
  ]);
};
const handleExport = (selectedRowKeys, data) => {
  const tbody = prepareSummaryList(selectedRowKeys, data);
  return exportExcel({
    data: [
      {
        thead: [
          'No.',
          'Ref.',
          'Application Submission Date',
          'Name of Project Leader',
          'Title',
          'Post',
          'Faculty',
          'Department',
          'Email',
          'Name of Project Member',
          'Project Title',
          'Commencement Date',
          'Completion Date',
          'Duration (Months)',
          'From TDG',
          'Matching Fund',
          'Matching Fund Funding Source',
          'Total',
        ],
        tbody,
      },
    ],
    filename: 'Funding Applications',
  });
};

const columns = [
  {
    title: 'Funding',
    key: 'setting',
    width: '20%',
    dataIndex: 'setting',
    sorter: true,
    render: (_, { id, setting }) => (
      <Link to={`/project-applications/${id}`}>{setting.title}</Link>
    ),
    colSize: 2,
    renderFormItem: renderProTableSearchProjectApplicationSetting,
  },
  {
    title: 'Faculty',
    key: 'faculty',
    dataIndex: 'faculty',
    render: (_, { hostDepartment, id }) => (
      <Link to={`/annual-reports/${id}`}>
        {hostDepartment?.faculty?.abbreviation}
      </Link>
    ),
    sorter: true,
    renderFormItem: (_, { type, defaultValue, ...rest }, form) =>
      renderProTableSearchFaculty(_, { type, defaultValue, ...rest }, form),
  },
  {
    title: 'Department',
    key: 'department',
    dataIndex: 'department',
    sorter: true,
    render: (_, { id, hostDepartment }) => (
      <Link to={`/project-applications/${id}`}>
        {hostDepartment?.abbreviation}
      </Link>
    ),
    renderFormItem: (_, { type, defaultValue, ...rest }, form) =>
      renderProTableSearchDepartment(
        _,
        {
          type,
          defaultValue,
          ...rest,
        },
        form
      ),
  },
  {
    title: 'Applicant',
    dataIndex: 'user',
    sorter: true,
    render: (_, { user, id }) => (
      <Link to={`/project-applications/${id}`}>{user.name}</Link>
    ),
    hideInSearch: true,
  },
  {
    title: 'Title',
    dataIndex: 'title',
    width: '30%',
    colSize: 2,
    sorter: true,
    render: (_, { id }) => <Link to={`/project-applications/${id}`}>{_}</Link>,
  },
  {
    title: 'Application submission date',
    key: 'submissionDate',
    dataIndex: 'firstSubmissionDate',
    sorter: true,
    render: (_, { firstSubmissionDate, id }) => (
      <Link to={`/project-applications/${id}`}>
        <DateDisplay date={firstSubmissionDate} />
      </Link>
    ),
    hideInSearch: true,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    valueType: 'select',
    valueEnum: ENUM_PROJECT_APPLICATION_STATUS,
    initialValue: 'APPROVED',
    sorter: true,
    render: (_, { id }) => <Link to={`/project-applications/${id}`}>{_}</Link>,
  },
];

const FundingApplications = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const { data, refetch } = useQuery(GET_PROJECT_APPLICATION_SUMMARY_LIST, {
    variables: {
      status: ['APPROVED'],
      showPriorities: [0, 2],
      pageSize: 0,
    },
  });
  const [, { loading, refetch: refetchDownload }] = useLazyQuery(
    DOWNLOAD_FUNDING_APPLICATION_PROPOSALS,
    {
      notifyOnNetworkStatusChange: true,
      onCompleted: (data) => {
        window.open(
          `${bulkDownloadUrl}?token=${data.downloadFundingApplicationProposals}`,
          '_blank'
        );
      },
    }
  );

  const allIds =
    data?.projectApplications?.nodes?.map(
      (projectApplication) => projectApplication.id
    ) ?? [];

  return (
    <Spin spinning={loading} tip="Gathering files to download">
      <Container title="Funding Applications" loading={false}>
        <ProTable
          columns={columns}
          options={{
            density: false,
          }}
          request={async (params, sort) => {
            setSelectedRowKeys([]);
            const { status, showPriorities } = getFundingApplicationStatus(
              params?.status
            );
            const { data } = await refetch({
              setting: params?.setting,
              department: params?.department,
              pageSize: undefined,
              faculty: params?.faculty,
              keyword: params?.title,
              showPriorities,
              status,
              sort: getSort(sort),
            });
            const dataSource = data?.projectApplications?.nodes;
            return Promise.resolve({
              data: dataSource,
              total: data?.projectApplications?.totalCount,
              success: true,
            });
          }}
          search={{
            searchText: 'Search',
            labelWidth: 'auto',
          }}
          pagination={{ defaultPageSize: 20, hideOnSinglePage: true }}
          rowKey="id"
          dataSource={data?.projectApplications?.nodes}
          scroll={false}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
          }}
          tableAlertRender={({ selectedRowKeys, onCleanSelected }) => (
            <>
              <Space size={24}>
                <span>
                  Selected {selectedRowKeys.length} Projects
                  <Button
                    type="link"
                    size="small"
                    style={{ marginLeft: 8 }}
                    onClick={onCleanSelected}
                  >
                    Cancel selected
                  </Button>
                </span>
              </Space>
            </>
          )}
          tableAlertOptionRender={({ selectedRowKeys }) => {
            return (
              <Space size={16}>
                <Button
                  type="primary"
                  onClick={() => {
                    handleExport(
                      selectedRowKeys,
                      data?.projectApplications?.nodes
                    );
                  }}
                  icon={<TableOutlined />}
                >
                  Export summary list
                </Button>
                <Button
                  type="primary"
                  onClick={() => {
                    refetchDownload({ ids: selectedRowKeys });
                  }}
                  icon={<FolderOutlined />}
                >
                  Download selected proposals
                </Button>
              </Space>
            );
          }}
          toolBarRender={() => [
            <SelectAllButton
              key="selectAll"
              selected={selectedRowKeys}
              setSelected={setSelectedRowKeys}
              allIds={allIds}
            />,
          ]}
        />
      </Container>
    </Spin>
  );
};

export default FundingApplications;
